import { useCallback } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';
import { useQueries } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { apiClient } from 'lib/api';
import queryKeys from './queryKeys';
import type { PinterestPinText } from './types';

export interface GetPinTextParams {
  app: string;
  flow: string;
  locale: string;
}

export const getPinText = async (
  { params }: { params: GetPinTextParams },
  signal?: AbortSignal,
): Promise<PinterestPinText> => {
  const res = await apiClient.get<PinterestPinText>(queryKeys.pinText, { params, signal });

  if (isAxiosError(res)) {
    const error = new Error('Get Pinterest Pin Text Failed');
    error.cause = res.response?.data;
    return Promise.reject(error);
  }

  return res.data;
};

export interface FlowPinTextResult {
  flow: string;
  data: PinterestPinText | undefined;
  isLoading: boolean;
  error: unknown;
}

export const usePinTexts = ({ app, flows, locale }: { app: string; flows: string[]; locale: string }) => {
  const combine = useCallback(
    (results: UseQueryResult<PinterestPinText, unknown>[]): FlowPinTextResult[] =>
      results.map((result, index) => ({
        flow: flows[index],
        data: result.data,
        isLoading: result.isPending,
        error: result.error,
      })),
    [flows],
  );

  return useQueries({
    queries: flows.map((flow) => ({
      queryKey: [queryKeys.pinText, app, flow, locale],
      queryFn: ({ signal }: { signal: AbortSignal }) => getPinText({ params: { app, flow, locale } }, signal),
      enabled: !!app && !!flow,
      staleTime: 0,
      refetchOnMount: true,
      retry: false,
    })),
    combine,
  });
};
